/*
 *   File : login.js
 *   Author : https://evoqins.com
 *   Description : Login page
 *   Integrations : js-cookie,react-toastify
 *   Version : 1.0.0
*/

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

//Custom components
import { CustomPasswordInput, CustomTextInput, OTPInput } from '../../Component/FormElements';
import { PrimaryButton } from '../../Component/Buttons';

import { APIService } from "../../Service";
import { setAccessToken } from "../../Helper/cookie";

const Login = () => {

    const inputRef = useRef(null);

    const navigate = useNavigate();

    const [name, setName] = useState("");
    const [nameError, setNameError] = useState("");
    const [password, setPassword] = useState("");
    const [passwordError, setPasswordError] = useState("");
    const [loading, setLoading] = useState(false);
    const [loginStep, setLoginStep] = useState(1);

    const [otpValue, setOtpValue] = useState("");
    const [focusedInput, setFocusedInput] = useState(2);
    const [confirmOTP, setConfirmOTP] = useState([]);
    const [otpError, setOtpError] = useState("");
    const [timer, setTimer] = useState(24);

    useEffect(() => {
        window.scrollTo({
            top: 0, behavior: 'smooth' //scroll top
        });
    }, []);

    useEffect(() => {
        setNameError("");
    }, [name]);

    useEffect(() => {
        setPasswordError("");
    }, [password]);

    useEffect(() => {
        setOtpError("");
    }, [otpValue]);

    useEffect(() => {
        if (timer === 0) return;

        const interval = setInterval(() => {
            setTimer(prev => prev - 1);
        }, 1000);

        return () => clearInterval(interval);
    }, [timer]);


    //handle login
    const _handleLogin = () => {
        let is_valid = true;
        if (name.trim().length === 0) {
            setNameError("Username is required");
            is_valid = false;
        }
        if (password.trim().length === 0) {
            setPasswordError("Password is required");
            is_valid = false;
        }

        if (is_valid === true) {
            _login();
            // setLoginStep(2);
        }
    };

    // API - login 
    const _login = () => {
        setLoading(true);
        let is_post = true;
        let url = 'auth/login';
        let data = {
            "username": name,
            "password": password
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setLoginStep(2);
            } else {
                toast.dismiss();
                toast.error(response.message, {
                    type: "error",
                    className: "e-toast",
                });
            }
            setLoading(false);
        });
    };

    // handle OTP 
    const _handleOTP = (e) => {
        if (loading !== true) {
            const input_value = e.target.value;
            if (input_value.length <= 6) {
                setOtpValue(input_value);
                const characters = [];

                for (let i = 0; i < input_value.length; i++) {
                    const character = input_value.charAt(i);
                    characters.push(character);
                }
                setConfirmOTP(characters)
            }
        }
    };

    // handle resend OTP
    const _handleResendOTP = () => {
        setOtpValue("");
        setConfirmOTP([]);
        _sentOTP();
    };

    // handle back to login 
    const _handleBack = () => {
        setOtpValue("");
        setConfirmOTP([]);
        setLoginStep(1);
    };

    // handle submit OTP 
    const _handleSubmitOTP = () => {
        if (otpValue.length < 6) {
            setOtpError("Enter received OTP");
            return;
        }
        if (otpValue.length === 6) {
            _verifyOTP();
        }
    };


    // API - Verify OTP 
    const _verifyOTP = () => {
        let is_post = true;
        let url = 'auth/verify-otp';
        let data = {
            "username": name,
            "otp": otpValue,
            "otp_type": "LOGIN"
        };
        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setOtpValue("");
                setConfirmOTP([]);
                const accessToken =
                    response?.data?.access_token || response?.access_token;
                const saved = setAccessToken(accessToken);
                if (!saved) {
                    toast.dismiss();
                    toast.error("Unable to save session. Please try again.", {
                        type: "error",
                        className: "e-toast",
                    });
                    return;
                }
                toast.success(response.message, {
                    type: "success",
                    className: "e-toast",
                });
                // Full reload so App.js reads the new cookie (same as fundsapp-admin)
                window.location.replace("/corridor-config");
            } else {
                toast.dismiss();
                toast.error(response.message, {
                    type: "error",
                    className: "e-toast",
                });
            }
        });
    };

    // API - Resend OTP (do not set access_token — same as fundsapp-admin)
    const _sentOTP = () => {
        let is_post = true;
        let url = 'auth/send-otp';
        let data = {
            "username": name,
            "otp_type": "LOGIN"
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setOtpValue("");
                setConfirmOTP([]);
                setTimer(24);
                toast.success(response.message, {
                    type: "success",
                    className: "e-toast",
                });
            } else {
                toast.dismiss();
                toast.error(response.message, {
                    type: "error",
                    className: "e-toast",
                });
            }
        });
    };

    return (
        <>
            <div className='e-auth-container e-bg-cover-size '>
                <div className='container '>
                    <div className='text-center'>
                        <img src={require("../../Assets/Images/Login/logo.svg").default}
                            draggable={false}
                            alt='Zappit-icon' />
                    </div>

                    {/* Login section */}
                    <div className='row padding-40px-top'>
                        <div className='offset-xl-4 offset-lg-3 offset-md-2 offset-sm-1 col-xl-4 col-lg-6 col-md-8 col-sm-10 '>
                            <div className='padding-30px-all e-bg-white-light e-border-radius-16 box-shadow-light-black'>
                                {loginStep === 1 ?
                                    // first step
                                    <>
                                        <h3 className='mb-3 e-primary-font e-font-24 line-height-32px e-text-rich-black e-font-weight-600'>
                                            Admin Login
                                        </h3>

                                        {/* user name */}
                                        <CustomTextInput label="Username"
                                            placeholder="Enter username"
                                            type="text"
                                            require
                                            value={name}
                                            error={nameError}
                                            autoFocus={true}
                                            className="w-100"
                                            labelStyle="e-font-weight-400"
                                            onKeyPress={_handleLogin}
                                            handleChange={(e) => setName(e)} />

                                        {/* password */}
                                        <CustomPasswordInput label="Password"
                                            placeholder="Enter password"
                                            require
                                            value={password}
                                            error={passwordError}
                                            className="w-100"
                                            labelStyle="e-font-weight-400"
                                            handleChange={(e) => setPassword(e)}
                                            onKeyPress={_handleLogin} />

                                        {/* forgot pw button */}
                                        <div className='text-end mb-3'>
                                            <span className='cursor-pointer text-decoration-hover-underline text-end e-font-14 e-primary-font line-height-18px e-text-primary-blue e-font-weight-500'
                                                onClick={() => navigate("/forgot-password")}>
                                                Forgot password?
                                            </span>
                                        </div>

                                        {/* login button */}
                                        <PrimaryButton name="Login"
                                            className="e-bg-primary-blue w-100"
                                            status={loading}
                                            onPress={_handleLogin} />
                                    </>
                                    :

                                    // otp step
                                    <>
                                        <img src={require("../../Assets/Images/Login/left-arrow.svg").default}
                                            draggable={false}
                                            className='cursor-pointer'
                                            alt='back-arrow'
                                            onClick={_handleBack} />
                                        <h3 className='padding-18px-top mb-4 e-primary-font e-font-24 line-height-32px e-text-rich-black e-font-weight-600'>
                                            OTP Verification
                                        </h3>
                                        {/* OTP Input */}
                                        <OTPInput label="Enter otp sent to the mail id"
                                            ref={inputRef}
                                            id="otp"
                                            require
                                            value={otpValue}
                                            spellcheck="false"
                                            error={otpError}
                                            focused_input={focusedInput}
                                            confirm_pin={confirmOTP}
                                            onFocus={() => setFocusedInput(2)}
                                            onBlur={() => setFocusedInput(0)}
                                            onKeyPress={_handleSubmitOTP}
                                            onChange={_handleOTP} />
                                        <p className=' mb-0 e-text-light-gray e-primary-font e-font-weight-400 line-height-18px e-font-14'>
                                            Didn’t get code yet?
                                            <span className='ps-1 e-font-14 e-primary-font line-height-18px e-text-primary-blue e-font-weight-500 cursor-pointer text-decoration-hover-underline'
                                                onClick={_handleResendOTP} >
                                                Resend OTP
                                            </span>
                                        </p>

                                        <div className='pt-4'>
                                            <PrimaryButton name="Confirm"
                                                className="e-bg-primary-blue w-100"
                                                onPress={_handleSubmitOTP} />
                                        </div>
                                    </>
                                }
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </>
    )
}

export default Login;